import {
  encodeQueryValue,
  getUrlByQueryParameterObj,
} from '@asset/function/queryStringFunctions';
import { authAPI } from '../core/instance';
import { toast } from 'react-toastify';
import {
  MaterialType,
  StudyContentPageType,
  StudyContentType,
  TotalCourse,
} from '@asset/enum/materialEnum';
import { LayoutType, ProblemStatusDetailType } from '@asset/type/common/type';
import {
  ProblemStatusType,
  SolvingResult,
  StudyItemType,
  StudyStep,
} from '@asset/enum/problemSolvingEnum';
import {
  AnomalyStatus,
  AnomalyType,
  AssignmentType,
  MyContentType,
} from '@asset/enum/studyContentEnum';
import { StudyItem } from '@asset/type/common/type';
import { CommonResponse, MediaContentMediaType } from '@apis/common/response';
import { Mastery, MasteryCountInfo } from '@asset/type/mastery';
import { ErrorReport } from '@asset/type/problemResultDetailViewType';
import { StudyPurpose } from '@asset/enum/enum';
import { DateTime } from 'luxon';
import qs from 'qs';
import _ from 'lodash';
import { OffsetPagination } from '@asset/type/common/response';

export interface ContentValue {
  problemStatusId: number;
  pageId: number;
  problemIndex: number;
  index: string;
  subIndex: string;
  // TODO:  type 이름 변경
  studyItemType: StudyItemType;
  problemLectureMediaTypes: MediaContentMediaType[];
  problemStatusDetails: ProblemStatusDetailType[];
  currentDetailType: StudyStep;
  studyHistory?: StudyHistory;
  problemStatusType: ProblemStatusType;
  isCompleted: boolean;
}
export interface StudyContentDetailValue extends ContentValue {
  studyContentId: number;
}
export interface ProblemListResponse {
  success: boolean;
  pagination: OffsetPagination;
  progressInfo: {
    totalProblemStatusCount: number;
    totalCompletedCount: number;
    progressRate: number;
    correctRate: number;
  };
  contentValues: ContentValue[];
  // TODO: contentType, studyType 구분 해서 타입 지정하기
  contentType: string;
  studyType: string;
  timeLimit: number;
}

export const getProblemList = async (
  pageType,
  studyContentId,
  pickedClassificationId,
  pickedClassificationType,
  problemFiltering,
  limit = 100,
  offset = 0,
): Promise<ProblemListResponse> => {
  let apiUrl = `/api/teachers/study-content-detail/${studyContentId}/?limit=${limit}&offset=${offset}`;
  if (problemFiltering) {
    problemFiltering
      .split(',')
      .map((filtering) => (apiUrl += `&${filtering}=${true}`));
  }
  if (pageType) apiUrl += `&pageType=${pageType}`;

  if (pickedClassificationId && pickedClassificationType) {
    apiUrl += `&classificationType=${pickedClassificationType}&classificationId=${pickedClassificationId}`;
  }

  const data = await authAPI
    .get<ProblemListResponse>(apiUrl)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return error.response.data;
    });
  return data;
};

export const getPageIdsOfStudyContentDetailPage = async (
  pageType: StudyContentPageType,
  studyContentId: number,
  parameters?: {
    classificationId?: number;
    classificationType?: string;
    isBookmark?: boolean;
    isFirstSolvingWrong?: boolean;
    isRubricGradeWrong?: boolean;
    isTeacherBookmark?: boolean;
    isWait?: boolean;
    notSolvedOnly?: boolean;
  },
): Promise<CommonResponse & { data: { pageIds: number[] } }> => {
  let apiUrl = `/api/teachers/study-content-detail/${studyContentId}/page-ids?pageType=${pageType}`;
  if (!_.isEmpty(parameters)) {
    apiUrl += `&${qs.stringify(parameters, { skipNulls: true })}`;
  }
  const data = await authAPI
    .get(apiUrl)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return error.response.data;
    });
  return data;
};
export const getMasteryInformation = async (
  query:
    | {
        studyContentType: StudyContentPageType;
        contentId: number;
        classificationId?: number;
        classificationType?: string;
        completedOnly?: boolean;
      }
    | {
        studentId: number;
        assignmentDueDateTimeRangeStart: string;
        assignmentDueDateTimeRangeEnd: string;
        completedOnly?: boolean;
      },
): Promise<{
  success: true;
  statistics: {
    masteryInfo: {
      studyItemCounts: {
        masteryLevel: Mastery;
        count: number;
      }[];
    };
  };
  masteryCountInfo: MasteryCountInfo;
}> => {
  let apiUrl = `/api/study-data/study-content-statistics/`;
  const _query = { ...query };
  if ('studyContentType' in query) {
    _query[
      query.studyContentType === StudyContentType.MY_ASSIGNMENT
        ? 'assignmentId'
        : query.studyContentType === StudyContentType.MY_STUDY_MATERIAL ||
          query.studyContentType === StudyContentType.MY_TEST
        ? 'myContentId'
        : 'myStudyResourceId'
    ] = query.contentId;
    if ('studyContentType' in _query) {
      delete _query.studyContentType;
    }
    if ('contentId' in _query) {
      delete _query.contentId;
    }
    if ('classificationId' in _query && _.isNil(_query.classificationId)) {
      delete _query.classificationId;
    }
    if ('classificationType' in _query && _.isNil(_query.classificationType)) {
      delete _query.classificationType;
    }
  }
  if ('assignmentDueDateTimeRangeStart' in query) {
    _query['assignmentDueDateTimeRangeStart'] =
      query.assignmentDueDateTimeRangeStart;
    _query['assignmentDueDateTimeRangeEnd'] =
      query.assignmentDueDateTimeRangeEnd;
  }
  apiUrl += '?' + qs.stringify(_query);

  const data = await authAPI
    .get(apiUrl)
    .then((response) => {
      const masteryCountInfo = {};
      _.map(
        response.data.statistics.masteryInfo.studyItemCounts,
        (masteryInfo) => {
          masteryCountInfo[masteryInfo.masteryLevel] = masteryInfo.count;
        },
      );
      return { ...response.data, masteryCountInfo };
    })
    .catch((error) => {
      return error.response.data;
    });
  return data;
};

export interface StudyHistory {
  pageId: number;
  problemStatusDetails: {
    id: number;
    problemStatusId: number;
    pageId: number;
    studyStep: StudyStep;
    solvingResult: SolvingResult;
    studentCompletedAt: string;
    myContent: {
      id: number;
      createdAt: string;
      subTitle: string;
    };
    assignments: [
      {
        id: number;
        createdAt: string;
        subTitle: string;
      },
    ];
  }[];
}

export const getStudyHistories = async (
  pageIds: number[],
  studentId: number,
): Promise<{ success: boolean; histories: StudyHistory[] }> => {
  const pageIdsQuery = encodeQueryValue(pageIds);
  const apiUrl = `/api/study-data/study-histories/?pageIds=${pageIdsQuery}&studentId=${studentId}`;

  const data = await authAPI
    .get(apiUrl)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return error.response.data;
    });
  return data;
};

export const getClassificationAndLastProblem = async (
  pageType,
  studyContentId,
  studentId,
  pageId,
) => {
  let apiUrl = `/api/study-data/tables/?pageType=${pageType}`;
  if (studyContentId) apiUrl += `&studyContentId=${studyContentId}`;
  if (studentId) apiUrl += `&studentId=${studentId}`;
  if (pageId) apiUrl += `&pageId=${pageId}`;

  const data = await authAPI
    .get(apiUrl)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return error.response.data;
    });
  return data;
};

export const getMaterialClassification = async (studyMaterialId) => {
  const apiUrl = `/api/materials/${studyMaterialId}/classification/`;

  const data = await authAPI
    .get(apiUrl)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return error.response.data;
    });
  return data;
};

export const getAssignmentPreview = async (studentId, requestData) => {
  const apiUrl = `/api/students/${studentId}/my-assignments/`;

  const data = await authAPI
    .post(apiUrl, requestData)
    .then((response) => {
      if (response) {
        return response.data;
      }
      return false;
    })
    .catch((error) => {
      return error.response.data;
    });
  return data;
};

interface CreateCustomMissionResponseData {
  success: boolean;
  studyContentId: number;
  overlappedProblemCount: number;
}
export const createCustomMission = async (requestData: {
  studyContentType: StudyContentType;
  studyContentId: number;
  checkedPageIdList?: number[] | null;
  subTitle: string;
  studyPurpose: StudyPurpose[];
  startDate: number;
  dueDate: number;
  timeLimit?: number;
  cutOffScore?: number;
  assignmentType: AssignmentType;
  excludeFromReview?: boolean;
}): Promise<CreateCustomMissionResponseData> => {
  const apiUrl = `/api/teachers/study-content/customize/`;
  const data = await authAPI
    .patch(apiUrl, requestData)
    .then((response) => {
      if (response) {
        return response.data;
      }
      return false;
    })
    .catch((error) => {
      if (error.response) toast.error(error.response.data.message);
      return error.response.data;
    });
  return data;
};

export const createCustomStudyMaterialOrMyContent = async (requestData: {
  checkedPageIdList: number[] | null;
  studyContentType: 'my-study-content' | StudyContentType;
  studyContentId: number;
  materialName: string;
  materialType?: MaterialType;
  myContentType: MyContentType;
  studentId?: number;
}): Promise<CreateCustomMissionResponseData> => {
  const apiUrl = `/api/teachers/study-content/customize/`;
  const data = await authAPI
    .patch(apiUrl, requestData)
    .then((response) => {
      if (response) {
        return response.data;
      }
      return false;
    })
    .catch((error) => {
      if (error.response) toast.error(error.response.data.message);
      return error.response.data;
    });
  return data;
};

export const updateMissionApiV2 = async (
  missionId: number,
  requestData: {
    subTitle: string;
    startDateTime: DateTime;
    endDateTime: DateTime;
    cutOffScore: number;
    timeLimit: number;
    pageIds: number[];
  },
): Promise<CommonResponse> => {
  const apiUrl = `/api/study-data/v2/assignments/${missionId}`;
  const convertedRequestData = {
    ...requestData,
    startDateTime: requestData.startDateTime.toISO(),
    endDateTime: requestData.endDateTime.toISO(),
  };

  const data = await authAPI
    .patch(apiUrl, convertedRequestData)
    .then((response) => {
      if (response) {
        return response.data;
      }
      return false;
    })
    .catch((error) => {
      if (error.response) toast.error(error.response.data.message);
      return error.response.data;
    });
  return data;
};

export const submitProgressModal = async (
  pageType,
  studyContentId,
  pageIds,
) => {
  const apiUrl = `/api/teachers/study-content-detail/check-progress/?studyContentId=${studyContentId}&pageType=${pageType}`;

  const data = await authAPI
    .patch(apiUrl, { pageIds: pageIds })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return error.response.data;
    });
  return data;
};

export const scoringApi = async (result: string, detailId: number) => {
  const apiUrl = `/api/teachers/problemstatusdetail/${detailId}/`;

  const data = await authAPI
    .patch(apiUrl, {
      solvingResult: result,
    })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return error.response.data;
    });
  return data;
};

export interface ModalStudyItemDataResponse {
  success: boolean;
  surroundPage: {
    previousPageId: number;
    previousStudyItemType: StudyItemType;
    nextPageId: number;
    nextStudyItemType: StudyItemType;
  };
  layoutType: LayoutType;
  studyItem: StudyItem;
  studyItemDetail: {
    isBookmark: boolean;
    subIndexName: string;
    studyItemType: StudyItemType;
    course: TotalCourse;
    materialType: MaterialType;
    materialName: string;
    indexNameType: string;
    indexName: string;
    indexNumber: number;
    division: string;
    subIndexNumber: number;
  };
  pagination?: {
    currPage: number;
    totalPage: number;
    nextPage: number | null;
    previousPage: number | null;
  };
}

export const getProblemStatusDetailModalReportApi = async (
  problemStatusDetailId: number,
): Promise<{
  success: boolean;
  errorReports: ErrorReport[];
  result: {
    isSolutionCheck: boolean;
    solvingResult: SolvingResult;
    studyStatusType: StudyStep;
    elapsedTime: number;
    chapter: string;
    section: string;
    indexNumber: number;
    indexName: string;
  };
}> => {
  const apiUrl = `/api/teachers/study-content-detail/modal/report/?problemStatusDetailId=${problemStatusDetailId}`;

  const data = await authAPI
    .get(apiUrl)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return error.response.data;
    });
  return data;
};
export const reStudyApi = async (detailId: number) => {
  const apiUrl = `/api/teachers/problemstatusdetail/${detailId}/`;

  const data = await authAPI
    .delete(apiUrl)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return error.response.data;
    });
  return data;
};

export const submitFeedbackApi = async (studyStatusType, reqData) => {
  const data = await authAPI
    .patch(`/api/study-data/feedback/?type=${studyStatusType}`, reqData)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return error.response.data;
    });
  return data;
};

export const patchHideContents = async (reqData) => {
  const data = await authAPI
    .patch(`/api/study-data/hide-contents/`, reqData)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return error.response.data;
    });
  return data;
};

export const getStudyContentProgressInfoApi = async (
  pageType: StudyContentPageType,
  studyContentId: number,
  queryString: {
    classificationId: number;
    classificationType: string;
  } | null,
): Promise<StudyContentProgressInfoResponse> => {
  let api = `/api/study-data/${pageType}/${studyContentId}/progress/`;
  if (queryString) api += getUrlByQueryParameterObj('', queryString);
  const data = await authAPI
    .get<StudyContentProgressInfoResponse>(api)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return error.response.data;
    });
  return data;
};

export interface StudyContentProgressInfoResponse extends CommonResponse {
  waitProblemCount: number;
  correctProblemCount: number;
  gradedProblemCount: number;
  completedProblemCount: number;
  totalProblemCount: number;
  progressRate: number;
  correctRate: number;
}

interface PatchAnomaliesStatusResponse extends CommonResponse {
  id: number;
  anomalyType: AnomalyType;
  status: AnomalyStatus;
}

export const patchAnomaliesStatus = async (
  anomalyId: number,
  requestBody: {
    status: AnomalyStatus;
  },
): Promise<PatchAnomaliesStatusResponse> => {
  const apiUrl = `/api/study-data/anomalies/${anomalyId}`;

  const data = await authAPI
    .patch<PatchAnomaliesStatusResponse>(apiUrl, requestBody)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return error.response.data;
    });
  return data;
};
